// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/DefinirSenha.tsx
//  Página PÚBLICA do link enviado por e-mail (boas-vindas do provisionamento
//  ou "esqueci minha senha"): consome o token de uso único e grava a senha.
//  ≠ /reset-password (troca de senha de usuário já logado).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient, getApiErrorMessage } from '@/lib/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { KeyRound, Loader2 } from 'lucide-react';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import logo from '@/assets/logo.jpg';

const schema = z.object({
  senha: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
  confirmar: z.string(),
}).refine((d) => d.senha === d.confirmar, {
  message: 'As senhas não coincidem',
  path: ['confirmar'],
});

export default function DefinirSenha() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [senha, setSenha] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const novaSenha = formData.get('senha') as string;
    const confirmar = formData.get('confirmar') as string;

    try {
      schema.parse({ senha: novaSenha, confirmar });
      await apiClient.post('/api/auth/definir-senha', { token, novaSenha });
      toast({
        title: 'Senha definida!',
        description: 'Agora entre com seu e-mail e a nova senha.',
      });
      navigate('/auth');
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({ title: 'Dados inválidos', description: error.errors[0].message, variant: 'destructive' });
      } else {
        toast({
          title: 'Erro',
          description: getApiErrorMessage(error, 'Não foi possível definir a senha.'),
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex justify-center">
          <img src={logo} alt="Udisys" className="h-14 w-auto object-contain" />
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">Definir senha</CardTitle>
            <CardDescription className="text-center">
              Escolha a senha que você usará para entrar no Udisys Gestor
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!token ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Link inválido. Abra o link mais recente enviado ao seu e-mail ou
                  solicite um novo em "Esqueci minha senha".
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/auth">Ir para o login</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="senha">Nova senha</Label>
                  <Input
                    id="senha"
                    name="senha"
                    type="password"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    required
                    disabled={loading}
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                  />
                  <PasswordStrengthIndicator password={senha} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmar">Confirmar senha</Label>
                  <Input
                    id="confirmar"
                    name="confirmar"
                    type="password"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    required
                    disabled={loading}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <KeyRound className="mr-2 h-4 w-4" />}
                  {loading ? 'Salvando...' : 'Salvar e entrar'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          Udisys Gestor — Sistema ERP
        </p>
      </div>
    </div>
  );
}
